export type UnderwritingUserRole = 'cp' | 'bdm' | 'importer';

type MixpanelLike = {
  track?: (event: string, payload?: Record<string, unknown>) => void;
};

type UnderwritingBasePayload = {
  leadId: string;
  userRole: UnderwritingUserRole;
  screen: string;
};

export type UnderwritingEventInput = {
  area: string;
  action: string;
  result: string;
  tag?: string;
  status?: string;
  batchId?: number | null;
  docCount?: number;
  errorCode?: string;
  payload?: Record<string, unknown>;
};

const sanitize = (value: string) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const getUnderwritingUserRole = (
  isBDM?: boolean,
  isCP?: boolean
): UnderwritingUserRole => {
  if (isBDM) return 'bdm';
  if (isCP) return 'cp';
  return 'importer';
};

// Never send PII: matched on snake_cased key segments, at any depth.
const SENSITIVE_KEY =
  /(^|_)(name|email|phone|ein|ssn|dob|address|street|zip|account_number|routing|note|notes|token)(_|$)/;

const isSensitiveKey = (key: string) =>
  SENSITIVE_KEY.test(key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase());

const stripSensitive = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stripSensitive);
  if (value && typeof value === 'object' && !(value instanceof Date)) {
    return Object.entries(value as Record<string, unknown>).reduce(
      (acc, [k, v]) => {
        if (!isSensitiveKey(k)) acc[k] = stripSensitive(v);
        return acc;
      },
      {} as Record<string, unknown>
    );
  }
  return value;
};

export const last4 = (value?: string | number | null) =>
  String(value ?? '')
    .replace(/\D/g, '')
    .slice(-4);

export const fileExtension = (fileName?: string) => {
  const match = /\.([a-z0-9]+)$/i.exec(fileName || '');
  return match ? match[1].toLowerCase() : '';
};

export const trackUnderwritingMixpanelEvent = (
  mixpanel: MixpanelLike | undefined,
  basePayload: UnderwritingBasePayload,
  event: UnderwritingEventInput
) => {
  if (!mixpanel?.track) return;
  // Analytics must never break the flow it observes.
  try {
    const eventName = `uw_${sanitize(basePayload.screen)}_${sanitize(
      event.area
    )}_${sanitize(event.action)}_${sanitize(event.result)}`;
    mixpanel.track(
      eventName,
      stripSensitive({
        leadId: basePayload.leadId,
        userRole: basePayload.userRole,
        screen: basePayload.screen,
        tag: event.tag || '',
        status: event.status || '',
        batchId: event.batchId ?? null,
        docCount: event.docCount ?? 0,
        errorCode: event.errorCode || '',
        ...(event.payload || {}),
      }) as Record<string, unknown>
    );
  } catch {
    // ignore
  }
};
